import { action, computed, observable } from 'mobx';
import { checkState } from '../../../utils/preconditions';
import { FieldSetType, UserFormState } from '../../common/userInfo/states/UserFormState';
import { scrollToTop } from '../../../utils/scroll';
import { translateErrorMessage } from '../../../api/error';
import { GoogleAnalyticsClient } from '../../../../commonv2/modules/analytics';
import { CardPaymentState } from '../../common/payment/states/CardPaymentState';
import { StepSpec } from '../../common/stepPage/StepIndicator';
import { RemoteResourceV2, updateRemoteResourceV2 } from '../../../../commonv2/api/RemoteResourceV2';
import moment from 'moment-timezone';
import { GlobalStates } from '../../../states/globalStates';
import { FieldState } from '../../../states/FormState';
import { EventDetails, EventId, EventChargeScope } from '../../../types/event';
import { EventApis, EventReservationApis } from '../../../api/event';
import { CurrentUserState } from '../../../states/CurrentUserState';
import { TokenType } from '../../../../commonv3/types/tokens';

export enum ReserverType {
    NEW_USER = 0,
    OLD_USER = 1,
    USER = 2,
}

export enum PageStep {
    INPUT = 0,
    CONFIRMATION = 1,
    COMPLETE = 2,
    ERROR = 3,
}

export const EVENT_RESERVATION_STEPS: StepSpec[] = [
    { id: PageStep.INPUT, subTitle: 'Step. 1', title: '支払い方法の選択', visibleOnError: true },
    { id: PageStep.CONFIRMATION, subTitle: 'Step. 2', title: '内容の確認', visibleOnError: true },
    { id: PageStep.COMPLETE, subTitle: 'Step. 3', title: '予約完了', visibleOnError: false },
    { id: PageStep.ERROR, subTitle: 'Step. 3', title: '予約が失敗しました', error: true, visibleOnError: true },
];

export class EventReservationPageState {
    @observable reserverType: ReserverType = null;
    @observable eventId: EventId = null;
    @observable event: RemoteResourceV2<EventDetails> = RemoteResourceV2.loading();
    @observable currentStep: PageStep = PageStep.INPUT;
    @observable userForm: UserFormState = new UserFormState();
    @observable reservationNote = new FieldState<string>('', false);
    @observable paymentMethodForm: CardPaymentState = new CardPaymentState();
    @observable processing = false;
    @observable error = false;
    @observable errorMessage: string = null;
    @observable loading = true;
    @observable membershipAvailable = false;
    @observable privacyPolicyAgreed = false;

    @computed
    get appliedPaymentType() {
        if (this.event.isEmpty() || this.event.get().charge_scope === EventChargeScope.ALL_USERS) {
            return 'credit_card';
        }
        return this.membershipAvailable ? 'token' : 'credit_card';
    }

    @computed
    get appliedPrice() {
        if (this.event.isEmpty() || this.appliedPaymentType === 'token') {
            return 0;
        }
        return this.event.get().price;
    }

    async init(eventId: EventId, userState: CurrentUserState) {
        this.loading = true;
        try {
            this.eventId = eventId;
            const event = await updateRemoteResourceV2(
                this.event,
                (v) => (this.event = v),
                () => EventApis.find(eventId)
            );
            await userState.reloadUser();
            this.userForm.init(userState.currentUser, FieldSetType.APPLICATION);
            if (userState.isSignedIn()) {
                this.membershipAvailable =
                    userState.availableTokens.findAffordableToken(TokenType.EVENT, 1, event.starts_at) != null;
                this.reserverType = ReserverType.USER;
                const card = await this.paymentMethodForm.loadOriginalCard();
                if (card) {
                    this.paymentMethodForm.useNewCard = false;
                } else {
                    this.paymentMethodForm.useNewCard = true;
                }
            } else {
                this.reserverType = ReserverType.NEW_USER;
            }
        } catch (e) {
            this.goToErrorPage(e);
        } finally {
            this.loading = false;
        }
    }

    isRequiredFieldsFilled() {
        return this.userForm.isRequiredFieldsFilled() && this.paymentMethodForm.isRequiredFieldsFilled();
    }

    @action
    async goToConfirmation() {
        checkState(this.currentStep === PageStep.INPUT);
        this.processing = true;
        try {
            let valid = true;
            if (this.userForm.needUserInput()) {
                await this.userForm.validate();
                valid = !this.userForm.hasErrors();
            }
            if (this.paymentMethodForm.useNewCard && this.event.get().price > 0) {
                await this.paymentMethodForm.validateCardInfo();
                valid = valid && this.paymentMethodForm.canSubmit();
            }
            if (valid) {
                this.currentStep = PageStep.CONFIRMATION;
            }
        } catch (e) {
            this.goToErrorPage(e);
        } finally {
            this.processing = false;
            scrollToTop();
        }
    }

    @action
    goBackToInput() {
        checkState(this.currentStep === PageStep.CONFIRMATION);
        this.currentStep = PageStep.INPUT;
    }

    @action
    goToErrorPage(error: any) {
        this.error = true;
        this.errorMessage = translateErrorMessage(error);
        this.currentStep = PageStep.ERROR;
    }

    @action
    async submit() {
        checkState(this.currentStep === PageStep.CONFIRMATION);
        this.processing = true;
        try {
            await this.doReserve();
            await GlobalStates.currentUser.reloadUser();
            this.reportReservation();
            this.currentStep = PageStep.COMPLETE;
        } catch (e) {
            this.goToErrorPage(e);
        } finally {
            this.processing = false;
            scrollToTop();
        }
    }

    private reportReservation() {
        const eventLabel = moment(this.event.get().starts_at).format('YYYYMMDD');
        const { currentUser } = GlobalStates.currentUser;
        const email = currentUser ? currentUser.email : this.userForm.getValue('email');
        GoogleAnalyticsClient.sendGaEvent('trial', 'event_reservation_complete', eventLabel, 0, {
            email: email,
        });
    }

    @action
    private async doReserve() {
        const reservationNote = this.reservationNote.value;
        if (this.userForm.needUserInput()) {
            await EventReservationApis.reserveWithUserForm(
                this.eventId,
                this.appliedPaymentType,
                this.userForm.asJson(),
                this.paymentMethodForm.newCardToken,
                reservationNote
            );
        } else {
            await EventReservationApis.reserve(
                this.eventId,
                this.appliedPaymentType,
                this.paymentMethodForm.newCardToken,
                reservationNote
            );
        }
    }

    @action.bound
    togglePrivacyPolicyAgreed() {
        this.privacyPolicyAgreed = !this.privacyPolicyAgreed;
    }
}
